<template>
  <div class="web-order-details">
    <div class="header">
      <h1>Web Order Details</h1>
    </div>

    <div v-if="isLoading" class="loading">Loading order details...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else>
      <!-- Order Details Section -->
      <div class="order-details-section">
        <h2>Order Information</h2>
        <div class="details-grid">
          <div class="detail-item">
            <label>Order Number:</label>
            <span>{{ order.OrderNumber }}</span>
          </div>
          <div class="detail-item">
            <label>Description:</label>
            <span>{{ order.Description }}</span>
          </div>
          <div class="detail-item">
            <label>Customer:</label>
            <span>{{ order.Customer }}</span>
          </div>
          <div class="detail-item">
            <label>Created By:</label>
            <span>{{ order.UserName }}</span>
          </div>
          <div class="detail-item">
            <label>Create Date:</label>
            <span>{{ formatDate(order.CreateDate) }}</span>
          </div>
          <div class="detail-item">
            <label>Status:</label>
            <span :class="['status-badge', getStatusClass(order.Status)]">
              {{ order.Status }}
            </span>
          </div>
        </div>
      </div>

      <!-- Line Items Section -->
      <div class="line-items-section">
        <div class="section-header">
          <h2>Line Items</h2>
          <button 
            v-if="!lineItems.length" 
            @click="fetchLineItems" 
            class="btn btn-primary"
            :disabled="isLoading"
          >
            {{ isLoading ? 'Loading...' : 'View Line Items' }}
          </button>
        </div>
        <div v-if="lineItems.length" class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Customer Part</th>
                <th>Hose</th>
                <th>Length</th>
                <th>Cut Length</th>
                <th>Fitting A</th>
                <th>Fitting B</th>
                <th>Orientation</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in lineItems" :key="item.LineID">
                <td>{{ item.CustPart }}</td>
                <td>{{ item.Hose }}</td>
                <td>{{ item.Length }}</td>
                <td>{{ item.CutLength }}</td>
                <td>{{ item.FittingA }}</td>
                <td>{{ item.FittingB }}</td>
                <td>{{ item.Orientation }}</td>
                <td>{{ item.Quantity }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'WebOrderDetailsView',
  data() {
    return {
      order: {},
      lineItems: [],
      isLoading: false,
      error: null,
      showLineItems: false
    };
  },
  methods: {
    async fetchOrderDetails() {
      console.log('Fetching order details...');
      this.isLoading = true;
      this.error = null;
      this.lineItems = []; // Clear line items when fetching new order
      this.showLineItems = false; // Reset line items visibility
      try {
        const orderId = this.$route.params.id;
        const token = localStorage.getItem('auth_token');
        
        // Fetch order details
        const orderResponse = await axios.get(`${process.env.VUE_APP_API_URL}/weborders/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('Order details received:', orderResponse.data);
        this.order = orderResponse.data;
        this.isLoading = false;

        // If we're on the weborderlineitems route, fetch line items immediately
        if (this.$route.path.includes('weborderlineitems')) {
          await this.fetchLineItems();
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        this.error = error.response?.data?.message || 'Error fetching order details';
        this.isLoading = false;
      }
    },
    async fetchLineItems() {
      console.log('Fetching line items...');
      this.isLoading = true;
      this.error = null;
      try {
        const orderId = this.$route.params.id;
        const token = localStorage.getItem('auth_token');
        
        const lineItemsResponse = await axios.get(`${process.env.VUE_APP_API_URL}/weborderlineitems/${orderId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('Line items received:', lineItemsResponse.data);
        this.lineItems = lineItemsResponse.data;
        this.showLineItems = true;
        this.isLoading = false;
      } catch (error) {
        console.error('Error fetching line items:', error);
        this.error = error.response?.data?.message || 'Error fetching line items';
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'pending':
          return 'status-pending';
        case 'in progress':
          return 'status-progress';
        case 'completed':
          return 'status-completed';
        case 'cancelled':
          return 'status-cancelled';
        default:
          return 'status-default';
      }
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchOrderDetails();
        }
      }
    }
  }
};
</script>

<style scoped>
.web-order-details {
  padding: 2rem;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
}

.back-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.back-button:hover {
  background-color: var(--primary-hover);
}

.order-details-section {
  background: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

h2 {
  color: var(--text-primary);
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item label {
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.detail-item span {
  color: var(--text-primary);
}

.line-items-section {
  background: var(--background);
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: var(--background-alt);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.data-table td {
  font-size: 0.875rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-progress {
  background-color: #dbeafe;
  color: #1e40af;
}

.status-completed {
  background-color: #dcfce7;
  color: #166534;
}

.status-cancelled {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-default {
  background-color: #f3f4f6;
  color: #374151;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
}

.error {
  background-color: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.5rem;
  margin: 1rem 0;
  text-align: center;
  border: 1px solid #fecaca;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-hover);
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .web-order-details {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }
}
</style> 